import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useForm, z, zodResolver } from "@/hooks/schema";
import { UnitySelectField } from "@/components/UnitySelectField";
import { Form } from "@/components/Form";
import { Check, CirclePlus } from "lucide-react";
import { useMemo, useState } from "react";
import { cn } from "@/lib/utils";
import { colors } from "@/utils/colors";
import { registerWorkSchedulesData } from "@/Api/workSchedules";
import { useToastNavigate } from "@/utils/toastNavigate";
import { LoaderSpin } from "@/components/Loaders";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { WEEKDAY_LABELS } from "@/utils/weekdays";
import { CopyDayPopover } from "../CopyDayPopover";
import { calculateWeeklyMinutes, formatMinutesAsHours } from "@/utils/workScheduleHours";

type DayState = {
  active: boolean;
  start_time: string;
  end_time: string;
  break_duration_minutes: string;
};

const emptyDay = (): DayState => ({
  active: false,
  start_time: "",
  end_time: "",
  break_duration_minutes: "",
});

const schema = z.object({
  unity_id: z.string({ message: "*requerido" }).min(1, "*requerido"),
  name: z.string({ message: "*requerido" }).min(1, "*requerido"),
  tolerance_minutes: z.string().optional(),
});

type SchemaType = z.infer<typeof schema>;

export const AddBtn = ({ updateFront }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [days, setDays] = useState<DayState[]>(() => WEEKDAY_LABELS.map(() => emptyDay()));

  const form = useForm<SchemaType>({
    resolver: zodResolver(schema),
    defaultValues: {
      unity_id: "",
      name: "",
      tolerance_minutes: "0",
    },
  });

  const { toast } = useToastNavigate();
  const [formState, setFormState] = useState<string>("default");

  const updateDay = (weekday: number, patch: Partial<DayState>) => {
    setDays((prev) => prev.map((day, index) => (index === weekday ? { ...day, ...patch } : day)));
  };

  const copyDay = (source: number, targets: number[]) => {
    setDays((prev) =>
      prev.map((day, index) =>
        targets.includes(index) ? { ...prev[source], active: true } : day
      )
    );
  };

  const weeklyMinutes = useMemo(
    () => calculateWeeklyMinutes(days.filter((day) => day.active)),
    [days]
  );

  const formSubmit = async (data: SchemaType) => {
    const activeDays = days
      .map((day, weekday) => ({ ...day, weekday }))
      .filter((day) => day.active);

    if (activeDays.length < 1) {
      toast({
        description: "Marque pelo menos um dia da semana.",
        variant: "destructive",
      });
      return;
    }

    if (activeDays.some((day) => !day.start_time || !day.end_time)) {
      toast({
        description: "Informe entrada e saída de todos os dias marcados.",
        variant: "destructive",
      });
      return;
    }

    setFormState("loading");
    const payload = {
      unity_id: data.unity_id,
      name: data.name,
      tolerance_minutes: Number(data.tolerance_minutes) || 0,
      days: activeDays.map((day) => ({
        weekday: day.weekday,
        start_time: day.start_time,
        end_time: day.end_time,
        break_duration_minutes: day.break_duration_minutes ? Number(day.break_duration_minutes) : null,
      })),
    };

    const { data: apiResponse, ok } = await registerWorkSchedulesData(payload);
    if (!ok) {
      setFormState("error");

      setTimeout(() => {
        setFormState("default");
      }, 1000);
      setIsOpen(false);
      return;
    }

    updateFront((prev) => {
      if (prev?.data.length < 15)
        return {
          ...prev,
          data: [apiResponse?.response, ...prev.data],
        };

      prev?.data.pop();
      return {
        ...prev,
        data: [apiResponse?.response, ...prev.data],
        last_page: prev.last_page + 1,
      };
    });

    form.reset();
    setDays(WEEKDAY_LABELS.map(() => emptyDay()));
    setFormState("default");
    setIsOpen(false);
    toast({
      description: apiResponse?.message,
      action: <Check color="#1edc2b" />,
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger>
        <Button
          variant="outline"
          className={cn("py-3 flex gap-1", `bg-primary`)}
        >
          <CirclePlus className="size-5" strokeWidth={1.75} />
          Adicionar Horário
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader className="flex flex-col gap-2">
          <DialogDescription className="text-lg">
            Adicionar Horário de Trabalho
          </DialogDescription>
          <DialogTitle className="text-2xl">Informações Básicas</DialogTitle>
        </DialogHeader>
        <Form
          onSubmit={form.handleSubmit(formSubmit)}
          {...form}
          className="space-y-3"
        >
          <Form.Row>
            <UnitySelectField />
          </Form.Row>

          <Form.Row>
            <Form.Field name="name">
              {({ field }) => (
                <Form.Item>
                  <Form.Label>
                    Nome <Form.Message />
                  </Form.Label>
                  <Form.Control>
                    <Input {...field} placeholder="Insira um nome" />
                  </Form.Control>
                </Form.Item>
              )}
            </Form.Field>
            <Form.Field name="tolerance_minutes">
              {({ field }) => (
                <Form.Item>
                  <Form.Label>Tolerância (minutos)</Form.Label>
                  <Form.Control>
                    <Input {...field} type="number" min={0} max={120} />
                  </Form.Control>
                </Form.Item>
              )}
            </Form.Field>
          </Form.Row>

          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium">Dias da semana</label>
            <div className="flex flex-col gap-2 max-h-[340px] overflow-y-auto pr-1">
              {WEEKDAY_LABELS.map((label, weekday) => {
                const day = days[weekday];
                return (
                  <div key={weekday} className="flex flex-col gap-2 rounded-md border p-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">{label}</span>
                      <Switch
                        checked={day.active}
                        onCheckedChange={(checked) => updateDay(weekday, { active: checked })}
                      />
                    </div>
                    {day.active && (
                      <>
                      <div className="grid grid-cols-3 gap-2">
                        <div className="flex flex-col gap-1">
                          <label className="text-xs text-muted-foreground">Entrada</label>
                          <Input
                            type="time"
                            value={day.start_time}
                            onChange={(e) => updateDay(weekday, { start_time: e.target.value })}
                          />
                        </div>
                        <div className="flex flex-col gap-1">
                          <label className="text-xs text-muted-foreground">Saída</label>
                          <Input
                            type="time"
                            value={day.end_time}
                            onChange={(e) => updateDay(weekday, { end_time: e.target.value })}
                          />
                        </div>
                        <div className="flex flex-col gap-1">
                          <label className="text-xs text-muted-foreground">Intervalo (minutos)</label>
                          <Input
                            type="number"
                            min={0}
                            max={240}
                            placeholder="Ex: 60"
                            value={day.break_duration_minutes}
                            onChange={(e) => updateDay(weekday, { break_duration_minutes: e.target.value })}
                          />
                        </div>
                      </div>
                      <CopyDayPopover
                        sourceWeekday={weekday}
                        onCopy={(targets) => copyDay(weekday, targets)}
                      />
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="flex items-center justify-between rounded-md border bg-zinc-100 dark:bg-[#202020] px-3 py-2">
            <span className="text-sm font-medium">Total semanal</span>
            <span className="text-sm font-semibold">{formatMinutesAsHours(weeklyMinutes)}</span>
          </div>

          <DialogFooter>
            <Button
              type="submit"
              className={cn("w-full", colors.bg_primary)}
              disabled={formState !== "default"}
            >
              {formState === "loading" && (
                <>
                  Adicionando <LoaderSpin />
                </>
              )}
              {formState === "default" && "Adicionar"}
              {formState === "error" && "Erro ao adicionar"}
            </Button>
          </DialogFooter>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
